"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const frameworks = [{
  seq: "01",
  name: "Organization Overview",
  description: "Learn about our mission as a nonprofit water supply provider. We are dedicated to transparency, community health, and the sustainable management of our local water resources for all members."
}, {
  seq: "02",
  name: "Water Infrastructure",
  description: "Understand the journey of your water. From source to tap, we maintain rigorous treatment processes and infrastructure standards to ensure every drop meets safety and quality requirements."
}, {
  seq: "03",
  name: "For Committee Members",
  description: "Access governance documents, meeting schedules, and policy guidelines. We provide the tools necessary for effective leadership and informed decision-making within our community."
}, {
  seq: "04",
  name: "For Operators & Readers",
  description: "Technical resources, safety protocols, and reporting templates for our operational staff. Standardized procedures ensure consistent service delivery and accurate meter reading across the network."
}, {
  seq: "05",
  name: "Community Information",
  description: "Stay informed about service updates, water quality reports, and conservation tips. We believe an informed community is the foundation of a resilient water supply system."
}, {
  seq: "06",
  name: "Contact & Support",
  description: "Need assistance? Reach out to our support team for billing inquiries, service requests, or emergency reporting. We are here to help you navigate any water-related concerns."
}];
export default function Resources() {
  const heading = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="10536" id="resource-library" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
        {}
        <div ref={heading.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 transition-all duration-500 ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="lg:col-span-7">
            <h1 className="font-default text-foreground" style={{
            fontSize: "clamp(3.25rem, 7.5vw, 7.5rem)",
            fontWeight: 500,
            letterSpacing: "-0.03em",
            lineHeight: "0.92"
          }}>
              Resource <em className="italic">library.</em>
            </h1>
          </div>
          <div className="lg:col-span-5 flex lg:items-start">
            <p className="text-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: "1.5"
          }}>
              Water Service Provider Guide serves as a central hub for community members, committee leaders, and operational staff. We provide clear, accessible information to ensure our nonprofit water supply remains reliable, transparent, and community-focused for everyone we serve.
            </p>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-24">
          <div className="border-t border-border">
            {frameworks.map((item, i) => <FrameworkRow key={item.seq} data-index={i} seq={item.seq} name={item.name} description={item.description} delay={i * 80} />)}
          </div>
        </div>
      </div>
    </section>;
}
function FrameworkRow({
  seq,
  name,
  description,
  delay,
  ...rest
}: {
  seq: string;
  name: string;
  description: string;
  delay: number;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} {...rest} className={`group relative border-b border-border py-10 transition-all duration-500 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      {}
      <span aria-hidden="true" className="pointer-events-none absolute left-0 top-0 w-[2px] h-0 bg-secondary transition-all duration-300 ease-out group-hover:h-full" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-6 items-start">
        {}
        <div className="lg:col-span-1">
          <span className="font-mono text-muted-foreground uppercase" style={{
          fontSize: "0.75rem",
          fontWeight: 400,
          letterSpacing: "0.04em",
          lineHeight: "1.4"
        }}>
            {seq}
          </span>
        </div>

        {}
        <div className="lg:col-span-4">
          <h3 className="font-default text-foreground transition-transform duration-300 ease-out group-hover:-translate-x-2" style={{
          fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
          fontWeight: 500,
          letterSpacing: "-0.015em",
          lineHeight: "1.08"
        }}>
            {name}
          </h3>
        </div>

        {}
        <div className="lg:col-span-5">
          <p className="text-muted-foreground" style={{
          fontSize: "1rem",
          fontWeight: 400,
          lineHeight: "1.55"
        }}>
            {description}
          </p>
        </div>

        {}
        <div className="lg:col-span-2 lg:text-right">
          <Link to="/GuidancePage" className="inline-flex items-center text-foreground relative">
            <span className="relative pb-1">
              {"Learn more \u2192"}
              <span aria-hidden="true" className="absolute left-0 bottom-0 h-px w-full bg-secondary transition-all duration-200 ease-out group-hover:w-[calc(100%+1em)]" />
            </span>
          </Link>
        </div>
      </div>
    </div>;
}